import {LocalDate} from '@js-joda/core';
import {Link} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import SearchIcon from '@material-ui/icons/Search';
import React, {useCallback, useMemo, useState} from 'react';
import Highlighter from 'react-highlight-words';
import ProgressBackdrop, {Progress} from '../../LoadingIndicator/ProgressBackdrop';
import {ClientServiceTeam, Project, ProjectSchedule, UNKNOWN_TEAM} from '../Utils/EmployeeSchedulesCompiler';
import {useScheduleContext} from '../Utils/ScheduleContextProvider';
import ProjectLock from './ProjectLock';
import RemoveProjectButton from './RemoveProjectButton';
import ScheduleCell from './ScheduleCell';

const useStyles = makeStyles(theme => ({
    header: {
        display: 'flex',
        alignItems: 'center',
    },
    title: {
        flexGrow: 1,
    },
    highlight: {
        backgroundColor: theme.palette.secondary.light,
    },
    notActiveProject: {
        backgroundColor: '#e0e0e0',
        '& td': {
            color: theme.palette.text.secondary,
        },
    },
}));

type Props = {
    employeeId : number;
    startDate : LocalDate;
    currentWeek : string;
    schedule : ProjectSchedule;
    onProjectViewClick ?: (project : Project) => void;
    highlightPhrase ?: string;
    teams : ClientServiceTeam[];
};

const ScheduleRow : React.FC<Props> = ({
    employeeId,
    startDate,
    currentWeek,
    schedule,
    onProjectViewClick,
    highlightPhrase,
    teams
} : Props) => {
    const classes = useStyles();
    const scheduleContext = useScheduleContext();
    const team = useMemo(() => {
        return teams.find(team => team.id === schedule.project.teamId) ?? UNKNOWN_TEAM
    }, [schedule.project.teamId, teams]);
    const [progress, setProgress] = useState<Progress | null>(null);

    const handleFillRight = useCallback(async (hours : number, startOfWeek : string) => {
        const weeks = schedule.weeks.filter(week => week.startOfWeek > startOfWeek).map(week => ({
            startOfWeek: week.startOfWeek,
            scheduled: week.startOfWeek <= startOfWeek ? week.scheduled : hours,
        }));

        await scheduleContext.fillRight(employeeId, schedule.project.id, weeks, setProgress);
        setProgress(null);
    }, [schedule.weeks, schedule.project.id, scheduleContext, employeeId]);

    const handleApplyToAllVisible = useCallback(async (hours : number, startOfWeek : string) => {
        await scheduleContext.applyToAllVisible(
            employeeId,
            schedule.project,
            startOfWeek,
            startDate,
            hours,
            setProgress,
        );
        setProgress(null);
    }, [employeeId, schedule.project, scheduleContext, startDate]);

    const isNotActive = !!schedule.project.status && schedule.project.status !== 'Active';

    return (
        <TableRow className={isNotActive ? classes.notActiveProject : undefined}>
            <TableCell>
                <div className={classes.header}>
                    {!isNotActive ? (
                        <ProjectLock
                            employeeId={employeeId}
                            projectId={schedule.project.id}
                            locked={schedule.locked}
                        />
                    ) : (
                        <span style={{width: 24, marginLeft: -3, display: 'inline-block'}}/>
                    )}
                    <div className={classes.title}>
                        {team.abbreviation} -
                        {schedule.project.jiraKey
                            ? <>
                                {'\u00A0'}
                                <Link
                                    color="inherit" target="_blank" rel="noopener"
                                    href={'https://soliant.atlassian.net/browse/' + schedule.project.jiraKey}
                                >
                                    ({schedule.project.jiraKey})</Link>
                                {'\u00A0'}
                            </>
                            : '\u00A0'}
                        {highlightPhrase ? (
                            <Highlighter
                                searchWords={[highlightPhrase]}
                                autoEscape
                                textToHighlight={schedule.project.name}
                                highlightClassName={classes.highlight}
                            />
                        ) : schedule.project.name}
                    </div>
                    {onProjectViewClick && (
                        <IconButton size="small" onClick={() => onProjectViewClick(schedule.project)}>
                            <SearchIcon fontSize="inherit"/>
                        </IconButton>
                    )}
                    <RemoveProjectButton employeeId={employeeId} projectId={schedule.project.id}/>
                </div>

                <ProgressBackdrop progress={progress}/>
            </TableCell>

            {schedule.weeks.map(week => (
                <ScheduleCell
                    key={week.startOfWeek}
                    employeeId={employeeId}
                    projectId={schedule.project.id}
                    startOfWeek={week.startOfWeek}
                    currentWeek={currentWeek}
                    schedule={week}
                    disabled={isNotActive}
                    onFillRight={isNotActive ? undefined : handleFillRight}
                    onApplyToAllVisible={
                        scheduleContext.multipleEmployeeSchedules && !isNotActive ? handleApplyToAllVisible : undefined
                    }
                />
            ))}
        </TableRow>
    );
};

export default ScheduleRow;
